import * as React from "react";
import { Button, Grid, Paper, Stack, Tooltip } from "@mui/material";
import OverviewIndexMetrics from "src/components/overview-index-metrics";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import TimelineOverview from "src/components/timeline-overview";
import styled from "styled-components";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useNavigate } from "react-router-dom";
import CustomTooltip from "src/components/custom-tooltip";
import GroupIcon from "@mui/icons-material/Group";

const SectionTitle = styled.h2`
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    margin: 0;
`;

const OverallIndexCard = styled(Paper)`
    height: 166px;
    width: 216px;
    border-radius: 14px 0 0 14px;
`;

const OverallIndexValue = styled.h2`
    font-size: 62px;
    font-weight: 500;
    line-height: 1;
    margin: 0;
`;

const BenchmarkText = styled.h2`
    font-size: 16px;
    font-weight: 500;
    margin: 0;
`;

const SubIndexCard = styled(Paper)`
    height: 166px;
    border-radius: 0;
`;

const IndexesChartCard = styled(Paper)`
    height: 166px;
    border-radius: 0 14px 14px 0;
`;

const CardTitle = styled.h2`
    font-size: 14px;
    font-weight: 400;
`;

const ColorIndicator = styled.div`
    height: 10px;
    width: 10px;
    border-radius: 2px;
    margin-left: 6px;
    background-color: ${({ theme }) => theme.palette.background.overall};
`;
const IndexesSection = ({
    OverviewApi,
    isTeams,
    teamId,
}: {
    OverviewApi: {
        data?: {
            mean: {
                work: number | null;
                collaboration: number | null;
                wellbeing: number | null;
            };
            orgBaseline: {
                work: number;
                collaboration: number;
                wellbeing: number;
                overall: number;
            };
            globalBaseline: {
                work: number;
                collaboration: number;
                wellbeing: number;
                overall: number;
            };
            timeseries: {
                work?: number;
                collaboration?: number;
                wellbeing?: number;
                overall?: number;
                rank?: number;
                datetime: string;
            }[];
        };
    };
    isTeams: boolean;
    teamId?: string;
}) => {
    if (!OverviewApi) return null;
    const navigate = useNavigate();
    const redirectToIndexesSettings = () => {
        if (teamId) return navigate(`/teams/${teamId}/indexes-settings`);
        return navigate("/indexes-settings");
    };

    return (
        <>
            <Stack
                direction="row"
                spacing={1}
                mb={1}
                alignItems="center"
                justifyContent="space-between"
            >
                <SectionTitle>Indexes</SectionTitle>
                <Button
                    startIcon={<ViewListIcon />}
                    onClick={() => {
                        redirectToIndexesSettings();
                    }}
                    style={{ width: "257px", fontSize: "12px" }}
                >
                    {isTeams ? "Configure Team Indexes" : "Configure Indexes"}
                </Button>
            </Stack>
            <Grid container justifyContent="center" spacing={1}>
                <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={1} pb={1}>
                        <OverallIndexCard>
                            <Stack
                                direction="column"
                                justifyContent="flex-start"
                                alignItems="center"
                            >
                                <Stack direction="row" alignItems="center" pt={2}>
                                    <CardTitle>Overall</CardTitle>
                                    <ColorIndicator name="overall" />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={OverviewApi}
                                    height="60px"
                                    width="50%"
                                    streamingProperty={OverviewApi.data && OverviewApi.data.mean}
                                    variant={undefined}
                                >
                                    {OverviewApi &&
                                        OverviewApi.data &&
                                        OverviewApi.data &&
                                        OverviewApi.data.mean && (
                                            <>
                                                <OverallIndexValue>
                                                    {(
                                                        ((OverviewApi.data.mean.work || 0) +
                                                            (OverviewApi.data.mean.collaboration ||
                                                                0) +
                                                            (OverviewApi.data.mean.wellbeing ||
                                                                0)) /
                                                        3
                                                    ).toFixed(1)}
                                                </OverallIndexValue>
                                                <Stack
                                                    direction="row"
                                                    alignItems="flex-start"
                                                    spacing={1}
                                                >
                                                    <Stack direction="column">
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="flex-start"
                                                            alignItems="center"
                                                            spacing={1}
                                                            style={{ width: "67%", opacity: "0.5" }}
                                                        />
                                                        {!isTeams && (
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="center"
                                                                alignItems="center"
                                                                spacing={1}
                                                                pt={1}
                                                                style={{
                                                                    width: "100%",
                                                                    opacity: "0.5",
                                                                }}
                                                            >
                                                                <CustomTooltip title="Company Overall Average">
                                                                    <GroupIcon />
                                                                </CustomTooltip>

                                                                <ShowWithSkeleton
                                                                    fetchAction={OverviewApi}
                                                                    height="70%"
                                                                    width="100%"
                                                                    streamingProperty={
                                                                        OverviewApi.data &&
                                                                        OverviewApi.data.orgBaseline
                                                                    }
                                                                    variant={undefined}
                                                                >
                                                                    {typeof OverviewApi.data
                                                                        .orgBaseline?.overall ===
                                                                    "number" ? (
                                                                        <BenchmarkText>
                                                                            {OverviewApi.data.orgBaseline?.overall.toFixed(
                                                                                1
                                                                            )}
                                                                        </BenchmarkText>
                                                                    ) : (
                                                                        <Tooltip title="No data avaible for this index">
                                                                            <BenchmarkText>
                                                                                N|D
                                                                            </BenchmarkText>
                                                                        </Tooltip>
                                                                    )}
                                                                </ShowWithSkeleton>
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            </>
                                        )}
                                </ShowWithSkeleton>
                            </Stack>
                        </OverallIndexCard>
                        <SubIndexCard style={{ width: "100%" }}>
                            <OverviewIndexMetrics fetchAction={OverviewApi} isTeams={isTeams} />
                        </SubIndexCard>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                    <IndexesChartCard isTeams={isTeams}>
                        <ShowWithSkeleton
                            fetchAction={OverviewApi}
                            height="100%"
                            width="100%"
                            variant="rounded"
                            streamingProperty={
                                OverviewApi.data && OverviewApi.data && OverviewApi.data.timeseries
                            }
                        >
                            <TimelineOverview
                                data={
                                    OverviewApi &&
                                    OverviewApi.data &&
                                    OverviewApi.data &&
                                    OverviewApi.data.timeseries
                                }
                            />
                        </ShowWithSkeleton>
                    </IndexesChartCard>
                </Grid>
            </Grid>
        </>
    );
};

export default IndexesSection;
